/**
 * 组织架构模块 - 枚举定义
 * 
 * @description
 * 定义车间、生产线、工位的类型、状态等枚举值
 * 
 * @module types/organization/enums
 * 
 * @对应后端文件
 * - Workshop Model: backend/app/Modules/Organization/Models/Workshop.php
 * - ProductionLine Model: backend/app/Modules/Organization/Models/ProductionLine.php
 * - Workstation Model: backend/app/Modules/Organization/Models/Workstation.php
 * 
 * @注意事项
 * 1. 枚举值必须与后端 Model 常量完全一致（包括大小写）
 * 2. 每个枚举值都需要添加中文注释
 * 3. 不要修改已有的枚举值，避免影响历史数据
 * 
 * @更新时机
 * - 后端新增车间/产线/工位类型时
 * - 后端新增状态值时
 * - 后端修改枚举值定义时
 * 
 * @验证方式
 * 1. 对照后端 Model 的常量定义（TYPE_*, STATUS_*, QC_TYPE_*）
 * 2. 确保枚举值大小写完全匹配
 * 3. 确认中文注释准确描述含义
 * 
 * @last-updated 2025-10-19
 * @author AI Assistant
 */

/**
 * 车间类型（汽车配件CNC加工行业）
 * @see Workshop::TYPE_*
 */
export enum WorkshopType {
  /** 数控车间 */
  CNC = 'CNC',
  /** 热处理车间 */
  HEAT_TREATMENT = 'HEAT_TREATMENT',
  /** 表面处理车间 */
  SURFACE_TREATMENT = 'SURFACE_TREATMENT',
  /** 总装车间 */
  ASSEMBLY = 'ASSEMBLY',
  /** 检测车间 */
  INSPECTION = 'INSPECTION',
  /** 仓储车间 */
  WAREHOUSE = 'WAREHOUSE',
  /** 专线车间 */
  DEDICATED = 'DEDICATED',
}

/**
 * 车间状态
 * @see Workshop::STATUS_*
 */
export enum WorkshopStatus {
  /** 停用 */
  INACTIVE = 0,
  /** 启用 */
  ACTIVE = 1,
}

/**
 * 生产线类型（汽车配件CNC加工行业）
 * @see ProductionLine::TYPE_*
 */
export enum ProductionLineType {
  /** 数控加工线 */
  CNC = 'CNC',
  /** 切割线 */
  CUTTING = 'CUTTING',
  /** 清洗线 */
  CLEANING = 'CLEANING',
  /** 热处理线 */
  HEAT_TREATMENT = 'HEAT_TREATMENT',
  /** 表面处理线 */
  SURFACE_TREATMENT = 'SURFACE_TREATMENT',
  /** 检测线 */
  INSPECTION = 'INSPECTION',
  /** 装配线 */
  ASSEMBLY = 'ASSEMBLY',
  /** 包装线 */
  PACKAGING = 'PACKAGING',
}

/**
 * 生产线状态
 * @see ProductionLine::STATUS_*
 */
export enum ProductionLineStatus {
  /** 停用 */
  INACTIVE = 0,
  /** 启用 */
  ACTIVE = 1,
  /** 维护中 */
  MAINTENANCE = 2,
}

/**
 * 工位类型
 * @see Workstation::TYPE_*
 */
export enum WorkstationType {
  /** 人工 */
  MANUAL = 'MANUAL',
  /** 半自动 */
  SEMI_AUTO = 'SEMI_AUTO',
  /** 全自动 */
  AUTO = 'AUTO',
}

/**
 * 工位状态
 * @see Workstation::STATUS_*
 */
export enum WorkstationStatus {
  /** 停用 */
  INACTIVE = 0,
  /** 空闲 */
  IDLE = 1,
  /** 工作中 */
  WORKING = 2,
  /** 故障 */
  FAULT = 3,
  /** 维护中 */
  MAINTENANCE = 4,
}

/**
 * 质检类型
 * @see Workstation::QC_TYPE_*
 */
export enum QualityCheckType {
  /** 首检 */
  FIRST = 'FIRST',
  /** 巡检 */
  PATROL = 'PATROL',
  /** 终检 */
  FINAL = 'FINAL',
}

